import type { Node, Edge } from '@xyflow/react';
import type { NodePosition } from './layout-types';

const DEFAULT_NODE_WIDTH = 180;
const DEFAULT_NODE_HEIGHT = 88;
const AXIS_PADDING_X = 80;
const AXIS_PADDING_Y = 48;

export type TimeAxisNodeData = {
  elapsedSeconds?: number;
  hopIndex?: number;
  laneHint?: number;
};

const readRank = (node: Node): number => {
  const data = node.data as TimeAxisNodeData | undefined;
  if (typeof data?.hopIndex === 'number') {
    return data.hopIndex;
  }
  if (typeof data?.elapsedSeconds === 'number') {
    return data.elapsedSeconds;
  }
  return 0;
};

export const packLanes = ({
  items,
}: {
  items: Array<{ id: string; start: number; end: number }>;
}): Map<string, number> => {
  const lanes: number[] = [];
  const assignment = new Map<string, number>();
  const sorted = [...items].sort((left, right) => left.start - right.start);

  for (const item of sorted) {
    const existingLane = lanes.findIndex((end) => end <= item.start);
    if (existingLane >= 0) {
      lanes[existingLane] = item.end;
      assignment.set(item.id, existingLane);
      continue;
    }
    lanes.push(item.end);
    assignment.set(item.id, lanes.length - 1);
  }

  return assignment;
};

export function getTimeAxisLayout({
  nodes,
  nodesep,
  ranksep,
}: {
  nodes: Node[];
  edges: Edge[];
  nodesep: number;
  ranksep: number;
}): Map<string, NodePosition> {
  const uniqueRanks = [...new Set(nodes.map(readRank))].sort(
    (left, right) => left - right,
  );
  const columnByRank = new Map(uniqueRanks.map((rank, index) => [rank, index]));
  const columnWidth = Math.max(ranksep, DEFAULT_NODE_WIDTH + 96);

  const laneItems = nodes.map((node) => {
    const start = readRank(node);
    return {
      id: node.id,
      start,
      end: start + 1,
    };
  });
  const lanes = packLanes({ items: laneItems });

  const computedLayout = new Map<string, NodePosition>();
  for (const node of nodes) {
    const width = node.measured?.width ?? DEFAULT_NODE_WIDTH;
    const height = node.measured?.height ?? DEFAULT_NODE_HEIGHT;
    const column = columnByRank.get(readRank(node)) ?? 0;
    const lane = lanes.get(node.id) ?? 0;
    computedLayout.set(node.id, {
      x: AXIS_PADDING_X + column * columnWidth - width / 2,
      y: AXIS_PADDING_Y + lane * (height + nodesep),
    });
  }

  return computedLayout;
}
